import { Controller } from 'stimulus';
import axios from 'axios';

const VALIDATE_CODES_PATH = '/items/validate_codes';

// Bulk-create: on change of code inputs, validate via API and store only available codes.
export default class extends Controller {
  static targets = [
    'quantity',
    'codePrefix',
    'codePrefixField',
    'codeMode',
    'autoWrapper',
    'generatedCodesPreview',
    'codesRangeWrapper',
    'codeRangeFrom',
    'codeRangeTo',
    'codesStatus',
    'codesStatusText',
    'quantityField',
    'codesField',
    'codeRangeFromField',
    'codeRangeToField',
  ];

  connect() {
    this.updateModeVisibility();
    this.syncPrefixHiddenField();
    if (this.hasQuantityFieldTarget && this.hasQuantityTarget) {
      this.quantityFieldTarget.value = this.quantityTarget.value;
    }
    requestAnimationFrame(() => this.validateCodes());
  }

  quantityChanged() {
    if (this.hasQuantityFieldTarget) {
      this.quantityFieldTarget.value = this.quantityTarget.value;
    }
    this.codesChanged();
  }

  codeModeChanged() {
    this.updateModeVisibility();
    this.codesChanged();
  }

  prefixChanged() {
    this.syncPrefixHiddenField();
    this.codesChanged();
  }

  codesChanged() {
    this.validateCodes();
  }

  updateModeVisibility() {
    const mode = this.hasCodeModeTarget ? this.codeModeTarget.value : 'auto';
    if (this.hasAutoWrapperTarget) {
      this.autoWrapperTarget.classList.toggle('-state-hidden', mode !== 'auto');
    }
    if (this.hasCodesRangeWrapperTarget) {
      this.codesRangeWrapperTarget.classList.toggle('-state-hidden', mode !== 'range');
    }
  }

  getCodePrefix() {
    return (this.codePrefixTarget?.value || '').trim();
  }

  validatePrefix() {
    const prefix = this.getCodePrefix();
    if (!prefix) return { valid: true, prefix: '' };
    if (!/^[A-Za-z]+$/.test(prefix)) {
      return { valid: false, prefix, error: 'Prefix must contain letters only.' };
    }
    if (prefix.length >= 10) {
      return { valid: false, prefix, error: 'Prefix is too long (max 9 letters).' };
    }
    return { valid: true, prefix };
  }

  syncPrefixHiddenField() {
    if (this.hasCodePrefixFieldTarget) {
      this.codePrefixFieldTarget.value = this.getCodePrefix();
    }
  }

  async validateCodes() {
    const quantity = parseInt(this.quantityTarget?.value, 10) || 0;
    const mode = this.hasCodeModeTarget ? this.codeModeTarget.value : 'auto';
    const prefixCheck = this.validatePrefix();

    this.syncPrefixHiddenField();
    this.syncRangeHiddenFields(mode);

    if (!prefixCheck.valid) {
      this.writeValidatedCodes([], mode);
      this.setReady(false, prefixCheck.error);
      this.updateGeneratedPreview([]);
      return;
    }

    if (quantity <= 0) {
      this.setReady(false, 'Enter quantity.');
      this.updateGeneratedPreview([]);
      return;
    }

    if (mode === 'range') {
      const candidates = this.buildRangeCodes();
      if (!candidates.codes.length) {
        this.setReady(false, candidates.error || 'Enter codes to validate.');
        this.updateGeneratedPreview([]);
        return;
      }

      await this.requestValidation({
        codes: candidates.codes,
        quantity,
        prefix: prefixCheck.prefix,
        auto: false,
        mode,
      });
      return;
    }

    // Auto generate: only quantity is required.
    await this.requestValidation({
      codes: [],
      quantity,
      prefix: '',
      auto: true,
      mode,
    });
  }

  async requestValidation({ codes, quantity, prefix, auto, mode }) {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const response = await axios.post(
        VALIDATE_CODES_PATH,
        { codes, quantity, prefix, auto },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
        }
      );

      const validated = response.data?.validated_codes || [];
      const ready = response.data?.ready === true;

      if (!ready) {
        const inUse = response.data?.in_use || [];
        const message =
          validated.length < quantity
            ? `Only ${validated.length} available code(s) (need ${quantity}).${
                inUse.length ? ` Already in use: ${inUse.join(', ')}` : ''
              }`
            : `Codes already in use: ${inUse.join(', ')}`;
        this.writeValidatedCodes(validated, mode);
        this.setReady(false, message);
        this.updateGeneratedPreview(validated, auto);
        return;
      }

      this.writeValidatedCodes(validated, mode);
      this.setReady(true, `${validated.length} code(s) validated and ready.`);
      this.updateGeneratedPreview(validated, auto);
    } catch (err) {
      this.writeValidatedCodes([], mode);
      this.setReady(false, err.response?.data?.message || 'Could not validate codes.');
      this.updateGeneratedPreview([]);
    }
  }

  buildRangeCodes() {
    const fromRaw = this.codeRangeFromTarget?.value?.trim() || '';
    const toRaw = this.codeRangeToTarget?.value?.trim() || '';
    if (!fromRaw || !toRaw) return { codes: [], error: null };
    if (!/^\d+$/.test(fromRaw) || !/^\d+$/.test(toRaw)) {
      return { codes: [], error: 'Range start and end must be numeric.' };
    }
    const start = parseInt(fromRaw, 10);
    const end = parseInt(toRaw, 10);
    if (end < start) return { codes: [], error: 'Range "to" must be at least "from".' };
    const codes = [];
    for (let n = start; n <= end; n += 1) {
      codes.push(String(n));
    }
    return { codes, error: null };
  }

  syncRangeHiddenFields(mode) {
    if (this.hasCodeRangeFromFieldTarget && this.hasCodeRangeFromTarget) {
      this.codeRangeFromFieldTarget.value = mode === 'range' ? this.codeRangeFromTarget.value : '';
    }
    if (this.hasCodeRangeToFieldTarget && this.hasCodeRangeToTarget) {
      this.codeRangeToFieldTarget.value = mode === 'range' ? this.codeRangeToTarget.value : '';
    }
  }

  writeValidatedCodes(codes, mode) {
    if (this.hasCodesFieldTarget) {
      this.codesFieldTarget.value = codes.join(',');
    }
    if (mode === 'range') {
      if (this.hasCodeRangeFromFieldTarget) this.codeRangeFromFieldTarget.value = '';
      if (this.hasCodeRangeToFieldTarget) this.codeRangeToFieldTarget.value = '';
    }
  }

  updateGeneratedPreview(codes, showInAutoMode = true) {
    if (!this.hasGeneratedCodesPreviewTarget) return;

    const mode = this.hasCodeModeTarget ? this.codeModeTarget.value : 'auto';
    if (mode !== 'auto' || !showInAutoMode || !codes.length) {
      this.generatedCodesPreviewTarget.classList.add('-state-hidden');
      this.generatedCodesPreviewTarget.textContent = '';
      return;
    }

    this.generatedCodesPreviewTarget.textContent = codes.join(', ');
    this.generatedCodesPreviewTarget.classList.remove('-state-hidden');
  }

  setReady(ready, message) {
    this.element.dataset.bulkReady = ready ? 'true' : 'false';

    if (this.hasCodesStatusTarget) {
      this.codesStatusTarget.classList.remove('-state-hidden');
    }
    if (this.hasCodesStatusTextTarget) {
      this.codesStatusTextTarget.style.color = ready ? '#28a745' : '#dc3545';
      this.codesStatusTextTarget.textContent = message;
    }

    const quantity = parseInt(this.quantityTarget?.value, 10) || 0;
    const codes = (this.codesFieldTarget?.value || '').split(/[\s,]+/).filter(Boolean);
    this.element.dispatchEvent(
      new CustomEvent('items--wizard-bulk:changed', {
        bubbles: true,
        detail: { quantity, codes, ready },
      })
    );
  }
}
